import { Link, createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { HideToggle } from '../components/HideToggle'
import { KeyRow } from '../components/KeyRow'
import { useDeleteGame, useGameKeys, useLibrary } from '../lib/admin-api'
import { useDebouncedValue } from '../lib/use-debounced-value'
import { initialsFromTitle, tintFromString } from '../lib/tint'

export const Route = createFileRoute('/admin/library')({
  component: LibraryPage,
})

const DAY_MS = 24 * 60 * 60 * 1000

function LibraryPage() {
  const library = useLibrary()
  const [filter, setFilter] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const debouncedFilter = useDebouncedValue(filter, 150)

  const games = (library.data ?? []).filter((game) =>
    game.title.toLowerCase().includes(debouncedFilter.trim().toLowerCase()),
  )

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Library</h1>
          <p className="mt-1 text-sm text-ink-soft">
            Every game with keys, including those that have run out.
          </p>
        </div>

        <input
          type="search"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder="Filter…"
          aria-label="Filter library"
          className="w-full rounded-lg border border-line bg-surface-1 px-3 py-2 text-sm text-ink outline-none placeholder:text-ink-muted focus:border-accent sm:w-56"
        />
      </div>

      {library.isPending ? (
        <p className="mt-8 text-sm text-ink-muted">Loading…</p>
      ) : games.length === 0 ? (
        <p className="mt-8 text-sm text-ink-soft">Nothing matches that filter.</p>
      ) : (
        <ul className="mt-6 overflow-hidden rounded-xl border border-line">
          {games.map((game) => (
            <GameRow
              key={game.id}
              game={game}
              expanded={expandedId === game.id}
              onToggle={() => setExpandedId(expandedId === game.id ? null : game.id)}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

type LibraryGame = NonNullable<ReturnType<typeof useLibrary>['data']>[number]

/** Removing a game imported by mistake — the wrong search result, usually. */
function DeleteGameButton({ gameId, title }: { gameId: string; title: string }) {
  const deleteGame = useDeleteGame()

  return (
    <div className="flex items-center gap-2">
      {deleteGame.isError ? (
        <span role="alert" className="text-xs text-bad">
          {(deleteGame.error as Error).message}
        </span>
      ) : null}
      <button
        type="button"
        disabled={deleteGame.isPending}
        onClick={() => {
          if (confirm(`Remove “${title}” from the library? It has no keys.`)) {
            deleteGame.mutate(gameId)
          }
        }}
        className="rounded px-2 py-1 text-[11px] text-ink-muted transition-colors hover:bg-bad/15 hover:text-bad disabled:opacity-40"
      >
        {deleteGame.isPending ? 'Removing…' : 'Remove game'}
      </button>
    </div>
  )
}

function GameRow({
  game,
  expanded,
  onToggle,
}: {
  game: LibraryGame
  expanded: boolean
  onToggle: () => void
}) {
  const keys = useGameKeys(expanded ? game.id : null)

  const expiryDays = game.soonestExpiry
    ? Math.ceil((new Date(game.soonestExpiry).getTime() - Date.now()) / DAY_MS)
    : null

  return (
    <li className="border-b border-line last:border-b-0">
      <div className="flex items-stretch bg-surface-1 transition-colors hover:bg-surface-2">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex min-w-0 flex-1 items-center gap-2.5 px-2.5 py-2.5 text-left sm:gap-3 sm:px-3"
      >
        <span
          className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded"
          style={{ backgroundColor: tintFromString(game.title) }}
        >
          {game.capsuleUrl ? (
            <img src={game.capsuleUrl} alt="" className="size-full object-cover" loading="lazy" />
          ) : (
            <span className="font-display text-[10px] font-semibold text-white/40">
              {initialsFromTitle(game.title)}
            </span>
          )}
        </span>

        <span className="min-w-0 flex-1">
          <span
            className={`block truncate text-sm font-medium ${
              game.isHidden ? 'text-ink-muted line-through decoration-ink-muted/40' : 'text-ink'
            }`}
          >
            {game.title}
          </span>
          <span className="block text-xs text-ink-muted">
            {game.totalKeys} total · {game.claimedKeys} claimed
          </span>
        </span>

        {expiryDays !== null && expiryDays <= 30 ? (
          <span className="shrink-0 rounded-full bg-warn/15 px-2 py-0.5 font-mono text-[11px] text-warn">
            {expiryDays}d
          </span>
        ) : null}

        {/* "4 available" is too wide for a phone beside a title, so the word
            goes and the colour carries the meaning. */}
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 font-mono text-[11px] sm:px-2.5 ${
            game.availableKeys === 0 ? 'bg-surface-3 text-ink-muted' : 'bg-good/15 text-good'
          }`}
        >
          {game.availableKeys}
          <span className="hidden sm:inline"> available</span>
        </span>

        <span aria-hidden className="ml-1 text-ink-muted">
          {expanded ? '−' : '+'}
        </span>
      </button>

      <span className="flex shrink-0 items-center border-l border-line px-1">
        <HideToggle gameId={game.id} isHidden={game.isHidden} />
      </span>

      <Link
        to="/admin/game/$id"
        params={{ id: game.id }}
        className="flex shrink-0 items-center border-l border-line px-2.5 text-xs text-ink-muted transition-colors hover:bg-surface-3 hover:text-ink sm:px-3"
      >
        Edit
      </Link>
      </div>

      {expanded ? (
        <div className="border-t border-line bg-surface-0 px-2 py-3 sm:px-3">
          {keys.isPending ? (
            <p className="text-xs text-ink-muted">Loading keys…</p>
          ) : (keys.data ?? []).length === 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-ink-muted">No keys on this game.</p>
              {/* Only offered once the game is empty — the server refuses
                  otherwise, and the button should agree with the server. */}
              <DeleteGameButton gameId={game.id} title={game.title} />
            </div>
          ) : (
            <ul className="space-y-1.5">
              {(keys.data ?? []).map((key) => (
                <KeyRow key={key.id} entry={key} />
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </li>
  )
}
